import {
  ActivitiesContentType,
  ASSESSMENT_ACTIVITIES,
  LanguageCodes,
} from 'global/constants';
import { pushDownloadActivitiesEvent } from 'approot/shared/download-pages/download-activities.utils';
import useDownloadPages from 'approot/shared/download-pages/download-pages';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { getClassToDoOrStudentToken } from 'approot/student-class-to-do/student-class-to-do.utils';
import { getPagesToDownload } from './student-download-activities.utils';
import type { BookmarkedSectionOverviewFragment } from '../../../shared/bookmarks/bookmark.graphql.generated';

export const useDownloadStudentPages = () => {
  const { showSuccess, showError } = useSnackbar();
  const { isLoading, hasError, handleDownloadPages } = useDownloadPages();

  const downloadLesson = ({
    contentId,
    contentType,
    selectedSections,
    assignedOverviews,
    name,
    language,
    assetKey,
    version,
    onComplete,
  }: {
    contentId: number;
    contentType: ActivitiesContentType;
    selectedSections: string[];
    assignedOverviews: BookmarkedSectionOverviewFragment[];
    name: string;
    language: LanguageCodes;
    assetKey: string;
    version: string;
    onComplete: () => void;
  }) => {
    const pages = getPagesToDownload(assignedOverviews, selectedSections);
    handleDownloadPages({
      fileName: `${name}-${language}`,
      pages,
      assetKey,
      sameAsOriginal: false,
      headerFields: {
        authorization: `Bearer ${getClassToDoOrStudentToken()}`,
      },
    }).then(success => {
      if (success) {
        showSuccess('Your Activities have been downloaded');
        pushDownloadActivitiesEvent({
          targetType: contentType,
          targetIdentifier: contentId,
          pageNumbers: pages,
          isPagesFiltered: false,
          version,
          language,
        });
        onComplete();
      } else {
        showError(
          'There was an error downloading your Activities. Please try again later.'
        );
        onComplete();
      }
    });
  };

  const downloadAssessment = ({
    contentId,
    selectedSections,
    assignedOverviews,
    name,
    language,
    assetKey,
    version,
    onComplete,
  }: {
    contentId: number;
    selectedSections: string[];
    assignedOverviews: BookmarkedSectionOverviewFragment[];
    name: string;
    language: LanguageCodes;
    assetKey: string;
    version: string;
    onComplete: () => void;
  }) => {
    const pages = getPagesToDownload(assignedOverviews, selectedSections);
    handleDownloadPages({
      fileName: `${name}-${language}`,
      pages,
      assetKey,
      sameAsOriginal: false,
      headerFields: {
        authorization: `Bearer ${getClassToDoOrStudentToken()}`,
      },
    }).then(success => {
      if (success) {
        showSuccess('Your Activities have been downloaded');
        pushDownloadActivitiesEvent({
          targetType: ASSESSMENT_ACTIVITIES,
          targetIdentifier: contentId,
          pageNumbers: pages,
          isPagesFiltered: false,
          version,
          language,
        });
        onComplete();
      } else {
        showError(
          'There was an error downloading your Activities. Please try again later.'
        );
        onComplete();
      }
    });
  };

  return {
    isLoading,
    hasError,
    downloadLesson,
    downloadAssessment,
  };
};
